import React, { useEffect, useRef, useState } from 'react';
import { FaHeart } from 'react-icons/fa6';
import { motion } from 'framer-motion';

interface CharmedOverlayProps {
    /** Callback to get the target position (yaw, pitch) for auto-rotation */
    onTargetGenerated?: (yaw: number, pitch: number) => void;
    /** Current viewer yaw position */
    currentYaw?: number;
    /** Current viewer pitch position */
    currentPitch?: number;
}

/**
 * CharmedOverlay - Displays a heart at a random position in the photosphere
 * The heart's position is converted to screen coordinates based on viewer position
 */
const CharmedOverlay: React.FC<CharmedOverlayProps> = ({
    onTargetGenerated,
    currentYaw = 0,
    currentPitch = 0,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);
    // Use state for target so it resets properly on remount
    const [target, setTarget] = useState<{ yaw: number; pitch: number } | null>(null);
    // Track if we've already generated a target for this mount
    const hasGeneratedRef = useRef(false);
    const onTargetGeneratedRef = useRef(onTargetGenerated);
    
    // Keep the callback ref updated
    useEffect(() => {
        onTargetGeneratedRef.current = onTargetGenerated;
    }, [onTargetGenerated]);

    // Generate random target position on mount only
    useEffect(() => {
        if (hasGeneratedRef.current) return; // Already generated this mount
        hasGeneratedRef.current = true;
        
        // Random yaw: full 360 degrees (-PI to PI)
        const randomYaw = (Math.random() * 2 - 1) * Math.PI;
        // Random pitch: limited to avoid extreme top/bottom (-60 to 60 degrees)
        const randomPitch = (Math.random() * 2 - 1) * (Math.PI / 3);
        
        setTarget({ yaw: randomYaw, pitch: randomPitch });
        
        // Notify parent of target position
        onTargetGeneratedRef.current?.(randomYaw, randomPitch);
        
        // Small delay before showing to allow position calculation
        setTimeout(() => setIsVisible(true), 100);
        
        // Reset on unmount so next mount generates fresh target
        return () => {
            hasGeneratedRef.current = false;
        };
    }, []); // Empty deps - only run once on mount

    // Calculate screen position directly (no useMemo to ensure it updates every render)
    let screenPosition: { x: number; y: number; isInFront: boolean } | null = null;
    
    if (target) {
        // Calculate the angular difference between camera direction and target
        let deltaYaw = target.yaw - currentYaw;
        const deltaPitch = target.pitch - currentPitch;

        // Normalize deltaYaw to -PI to PI range
        while (deltaYaw > Math.PI) deltaYaw -= 2 * Math.PI;
        while (deltaYaw < -Math.PI) deltaYaw += 2 * Math.PI;

        // Approximate FOV mapping (assuming ~75 degree FOV)
        // Convert angular difference to screen percentage
        const fovH = (90 * Math.PI) / 180; // Horizontal FOV in radians
        const fovV = (90 * Math.PI) / 180; // Approximate vertical FOV

        // Map to screen coordinates (0 = left/top, 100 = right/bottom)
        const x = 50 - (deltaYaw / fovH) * 100;
        const y = 50 - (deltaPitch / fovV) * 100;

        // Check if target is roughly in front of the camera
        const isInFront = Math.abs(deltaYaw) < Math.PI / 2;

        screenPosition = { x, y, isInFront };
    }

    if (!isVisible || !screenPosition) return null;

    // Clamp position but show indicator at edge if out of view
    const clampedX = Math.max(5, Math.min(95, screenPosition.x));
    const clampedY = Math.max(5, Math.min(95, screenPosition.y));
    const isOnScreen = screenPosition.x >= 0 && screenPosition.x <= 100 && 
                       screenPosition.y >= 0 && screenPosition.y <= 100 &&
                       screenPosition.isInFront;

    return (
        <div ref={containerRef} className="absolute inset-0 w-full h-full pointer-events-none z-[15]">
            {/* Heart indicator */}
            <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ 
                    scale: isOnScreen ? [1, 1.2, 1] : [0.7, 0.9, 0.7],
                    opacity: screenPosition.isInFront ? (isOnScreen ? 1 : 0.6) : 0.3,
                    x: "-50%",
                    y: "-50%",
                }}
                transition={{
                    scale: {
                        repeat: Infinity,
                        duration: 1,
                        ease: "easeInOut",
                    },
                    opacity: {
                        duration: 0.3,
                    }
                }}
                style={{
                    position: 'absolute',
                    left: `${clampedX}%`,
                    top: `${clampedY}%`,
                    transform: 'translate(-50%, -50%)',
                }}
            >
                <div className="relative">
                    {/* Glow effect */}
                    <div 
                        className="absolute blur-lg"
                        style={{
                            background: 'radial-gradient(circle, rgba(255, 105, 180, 0.8) 0%, transparent 70%)',
                            width: '120px',
                            height: '120px',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                        }}
                    />
                    {/* Heart icon */}
                    <FaHeart 
                        size={(() => {
                            const small = typeof window !== 'undefined' && containerRef.current && containerRef.current.clientWidth < 501;
                            return isOnScreen ? (small ? 20 : 80) : (small ? 10 : 40);
                        })()}
                        className="drop-shadow-lg"
                        style={{
                            color: '#FFD9E4',
                            display: 'block',
                        }}
                    />
                </div>
            </motion.div>

            {/* Pink vignette effect */}
            <div 
                className="absolute inset-0 pointer-events-none"
                style={{
                    background: 'radial-gradient(ellipse at center, transparent 50%, rgba(255, 105, 180, 0.75) 100%)',
                }}
            />
        </div>
    );
};

export default CharmedOverlay;
